import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { MovableModal } from '@flipster/features/src/shared/movable-modal';
import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';

import InputStep from './AddPositionStep';
import ReviewStep from './OrderReviewStep';
import { AddPositionModalContext, addPositionModalBunja } from './state';
import { addPositionValidationBunja } from './validation-state';

interface Props {
  position: OpenPosition | LegacyOpenPosition;
  onSuccess?: (successOrderType: 'market' | 'limit') => void;
}

type AddPositionModalSteps = {
  input: undefined;
  review: undefined;
};

const Steps = createStepComponent<AddPositionModalSteps>();

interface AddPositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit?: () => void;
}

const AddPositionModal = ({
  isOpen,
  onClose,
  position: initPosition,
  onSuccess,
  onExit,
}: AddPositionModalProps & Props) => {
  return (
    <SymbolContext value={initPosition.symbol}>
      <AddPositionModalContext value={initPosition}>
        <MovableModal.Root onClose={onClose} isOpen={isOpen} onExit={onExit}>
          <ModalErrorBoundary onError={onClose}>
            <AddPositionModalContent onSuccess={onSuccess} onClose={onClose} />
          </ModalErrorBoundary>
        </MovableModal.Root>
      </AddPositionModalContext>
    </SymbolContext>
  );
};

const AddPositionModalContent = ({ onSuccess, onClose }: Omit<Props, 'position'> & { onClose: () => void }) => {
  const t = useTranslations();
  const { isMobile } = useBreakpoint();
  const { addPosition, isLoadingAtom, tabAtom, handleSegmentTrack } = useBunja(addPositionModalBunja);
  const { isReviewDisabledAtom } = useBunja(addPositionValidationBunja);
  const isLoading = useAtomValue(isLoadingAtom);
  const isReviewDisabled = useAtomValue(isReviewDisabledAtom);
  const tab = useAtomValue(tabAtom);

  const { current, push } = useSteps<AddPositionModalSteps>({
    initialValue: {
      step: 'input',
      context: undefined,
    },
  });

  const handleNext = async () => {
    switch (current.step) {
      case 'input': {
        handleSegmentTrack('input');
        push({ step: 'review', context: undefined });
        break;
      }
      case 'review': {
        handleSegmentTrack('review');
        await addPosition();
        onSuccess?.(tab);
        onClose();
        break;
      }
    }
  };

  const handleBack = () => {
    switch (current.step) {
      case 'review':
        push({ step: 'input', context: undefined });
        break;
      default:
        onClose();
        break;
    }
  };

  const backLabel = current.step === 'input' ? t('Common.r250401.Cancel') : t('Common.r250401.Back');
  const nextLabel = current.step === 'input' ? t('Trade.r250401.Review') : t('Trade.r250401.Place_order');

  return (
    <>
      <Steps model={current}>
        <Steps.Step step='input'>{() => <InputStep />}</Steps.Step>
        <Steps.Step step='review'>{() => <ReviewStep />}</Steps.Step>
      </Steps>

      {/* FIXME: Footer가 Fragment로 쌓이면, 모바일에서 제대로 레이아웃이 잡히지 않는 이슈가 있어서 step화 시키지 않음 */}
      <MovableModal.Footer align={isMobile ? 'vertical' : 'horizontal'}>
        <MovableModal.Button
          label={backLabel}
          size='large'
          hierarchy='secondary'
          color='neutral'
          disabled={isLoading}
          onClick={handleBack}
        />
        <MovableModal.Button
          label={nextLabel}
          size='large'
          withSpinner
          isLoading={isLoading}
          disabled={isReviewDisabled}
          onClick={handleNext}
        />
      </MovableModal.Footer>
    </>
  );
};

export default AddPositionModal;
